import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { recurringInvoicesApiService, RecurringInvoiceCreateDTO, RecurringInvoiceUpdateDTO, RecurringInvoiceFilters } from '../api/recurring-invoices-api';
import { RecurringInvoice } from '../types/recurring-invoice.types';

// Estado inicial
interface RecurringInvoicesState {
  recurringInvoices: RecurringInvoice[];
  currentRecurringInvoice: RecurringInvoice | null;
  loading: boolean;
  error: string | null;
  filters: RecurringInvoiceFilters;
}

const initialState: RecurringInvoicesState = {
  recurringInvoices: [],
  currentRecurringInvoice: null,
  loading: false,
  error: null,
  filters: {},
};

// Async Thunks
export const fetchRecurringInvoices = createAsyncThunk(
  'recurringInvoices/fetchRecurringInvoices',
  async (filters: RecurringInvoiceFilters = {}, { rejectWithValue }) => {
    try {
      const response = await recurringInvoicesApiService.getRecurringInvoices(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar facturas recurrentes'
      );
    }
  }
);

export const fetchRecurringInvoice = createAsyncThunk(
  'recurringInvoices/fetchRecurringInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await recurringInvoicesApiService.getRecurringInvoiceById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar factura recurrente'
      );
    }
  }
);

export const createRecurringInvoice = createAsyncThunk(
  'recurringInvoices/createRecurringInvoice',
  async (data: RecurringInvoiceCreateDTO, { rejectWithValue }) => {
    try {
      const response = await recurringInvoicesApiService.createRecurringInvoice(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear factura recurrente'
      );
    }
  }
);

export const updateRecurringInvoice = createAsyncThunk(
  'recurringInvoices/updateRecurringInvoice',
  async (
    { id, data }: { id: string; data: RecurringInvoiceUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await recurringInvoicesApiService.updateRecurringInvoice(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar factura recurrente'
      );
    }
  }
);

export const deleteRecurringInvoice = createAsyncThunk(
  'recurringInvoices/deleteRecurringInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      await recurringInvoicesApiService.deleteRecurringInvoice(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar factura recurrente'
      );
    }
  }
);

export const generateInvoices = createAsyncThunk(
  'recurringInvoices/generateInvoices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await recurringInvoicesApiService.generateInvoices();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al generar facturas'
      );
    }
  }
);

// Slice
const recurringInvoicesSlice = createSlice({
  name: 'recurringInvoices',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<RecurringInvoiceFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentRecurringInvoice: (state, action: PayloadAction<RecurringInvoice | null>) => {
      state.currentRecurringInvoice = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch recurring invoices
    builder
      .addCase(fetchRecurringInvoices.pending, state => {
        if (state.recurringInvoices.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchRecurringInvoices.fulfilled, (state, action) => {
        state.loading = false;
        // Los datos ya vienen mapeados desde el API service
        const payload = action.payload;
        
        if (Array.isArray(payload) && payload.length > 0) {
          state.recurringInvoices = payload.filter((invoice: any) => 
            invoice !== null && 
            invoice !== undefined && 
            typeof invoice === 'object' &&
            invoice.id // Asegurar que tenga al menos un ID
          );
        } else {
          state.recurringInvoices = [];
        }
      })
      .addCase(fetchRecurringInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch recurring invoice
    builder
      .addCase(fetchRecurringInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecurringInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRecurringInvoice = action.payload as RecurringInvoice;
      })
      .addCase(fetchRecurringInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create recurring invoice
    builder
      .addCase(createRecurringInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRecurringInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const newInvoice = action.payload as RecurringInvoice;
        state.recurringInvoices.unshift(newInvoice);
      })
      .addCase(createRecurringInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update recurring invoice
    builder
      .addCase(updateRecurringInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecurringInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const updatedInvoice = action.payload as RecurringInvoice;
        const index = state.recurringInvoices.findIndex(
          invoice => invoice.id === updatedInvoice.id
        );
        if (index !== -1) {
          state.recurringInvoices[index] = updatedInvoice;
        }
        if (state.currentRecurringInvoice?.id === updatedInvoice.id) {
          state.currentRecurringInvoice = updatedInvoice;
        }
      })
      .addCase(updateRecurringInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete recurring invoice
    builder
      .addCase(deleteRecurringInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecurringInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.recurringInvoices = state.recurringInvoices.filter(
          invoice => invoice.id !== action.payload
        );
        if (state.currentRecurringInvoice?.id === action.payload) {
          state.currentRecurringInvoice = null;
        }
      })
      .addCase(deleteRecurringInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Generate invoices
    builder
      .addCase(generateInvoices.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateInvoices.fulfilled, (state, action) => {
        state.loading = false;
        // Actualizar lastGeneratedDate de las facturas recurrentes afectadas
        const result = action.payload as any;
        if (result.generated > 0) {
          // Refresh the list
        }
      })
      .addCase(generateInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentRecurringInvoice,
  clearError,
} = recurringInvoicesSlice.actions;

// Selectors
export const selectRecurringInvoices = (state: { recurringInvoices: RecurringInvoicesState }) => state.recurringInvoices.recurringInvoices;
export const selectCurrentRecurringInvoice = (state: { recurringInvoices: RecurringInvoicesState }) => state.recurringInvoices.currentRecurringInvoice;
export const selectRecurringInvoicesLoading = (state: { recurringInvoices: RecurringInvoicesState }) => state.recurringInvoices.loading;
export const selectRecurringInvoicesError = (state: { recurringInvoices: RecurringInvoicesState }) => state.recurringInvoices.error;
export const selectRecurringInvoicesFilters = (state: { recurringInvoices: RecurringInvoicesState }) => state.recurringInvoices.filters;

export default recurringInvoicesSlice.reducer;
